import React, { useEffect, useRef, useState } from "react";
import { Field, useFormSubmit, FormFooter } from "./_shared";
import { api } from "../../lib/api";
import { useContent } from "../../lib/content";
import { usePersonalization } from "../../lib/personalization";
import { Save } from "lucide-react";

/**
 * Buyer Welcome Info — under-contract form. Captures the questions every
 * brand-new homeowner asks in their first week (trash day, garage code,
 * alarm code, neighborhood tips). The agent hands the resulting branded
 * PDF to the buyer at closing.
 *
 * Field set is fully CMS-driven:
 *   1. Per-seller override (`personalization.buyer_info_fields_override`) wins
 *      if non-empty — lets agents tailor the form for an unusual property.
 *   2. Otherwise, the agent's CMS list (`site_content.buyer_info_fields`).
 *   3. Otherwise, the system defaults baked into DEFAULT_CONTENT.
 *
 * Each field has `kind` ("short" or "long") to drive layout. UX matches
 * HomeStoryForm — auto-saves drafts every 1s, submit emails the PDF.
 */

const DRAFT_ENDPOINT = "/forms/draft/buyer_info";

export default function BuyerInfoForm({ onSubmitted }) {
  const { content } = useContent();
  const { p } = usePersonalization();
  // Resolve fields: per-seller override (if any) → agent CMS → defaults.
  const fields = (p?.buyer_info_fields_override && p.buyer_info_fields_override.length > 0)
    ? p.buyer_info_fields_override
    : (content?.buyer_info_fields || []);
  const shortFields = fields.filter((f) => f.kind !== "long");
  const longFields = fields.filter((f) => f.kind === "long");

  const [values, setValues] = useState({});
  const [savedAt, setSavedAt] = useState(null);
  const saveTimer = useRef(null);
  const loaded = useRef(false);
  const { submit, busy, err } = useFormSubmit("buyer_info");

  // Hydrate from any saved draft so refreshes don't lose work.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(DRAFT_ENDPOINT);
        if (alive) setValues(data.data || {});
      } catch {
        // No draft yet.
      } finally {
        loaded.current = true;
      }
    })();
    return () => { alive = false; };
  }, []);

  // Debounced auto-save — same 1s window as HomeStoryForm so the UX matches.
  useEffect(() => {
    if (!loaded.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.put(DRAFT_ENDPOINT, { data: values });
        setSavedAt(new Date());
      } catch {
        // Silent — transient draft saves shouldn't interrupt the flow.
      }
    }, 1000);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [values]);

  const upd = (k) => (e) => setValues((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); submit(values, onSubmitted); }}
      className="card"
      style={{ padding: 32, display: "flex", flexDirection: "column", gap: 24 }}
      data-testid="buyer-info-form"
    >
      <div style={{ color: "var(--text-muted)", fontSize: 14, lineHeight: 1.55, padding: "12px 16px", background: "#F0EDE3", borderRadius: 12, border: "1px solid var(--border)" }}>
        <b style={{ display: "block", marginBottom: 4, color: "var(--text)" }}>A welcome packet for your buyer.</b>
        Every new homeowner has the same questions in their first week — trash day, garage code, Wi-Fi.
        Fill in what you can; you can save now and finish later. We'll wrap it into a branded PDF
        your agent can hand to the buyer at closing.
      </div>

      {shortFields.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
          {shortFields.map((f) => (
            <Field key={f.key} label={f.label} hint={f.hint}>
              <input
                data-testid={`buyer-info-${f.key}`}
                className="input-field"
                value={values[f.key] || ""}
                onChange={upd(f.key)}
                placeholder={f.placeholder || ""}
              />
            </Field>
          ))}
        </div>
      )}

      {longFields.map((f) => (
        <Field key={f.key} label={f.label} hint={f.hint}>
          <textarea
            data-testid={`buyer-info-${f.key}`}
            className="input-field"
            rows={4}
            value={values[f.key] || ""}
            onChange={upd(f.key)}
            placeholder={f.placeholder || ""}
            style={{ resize: "vertical", fontFamily: "inherit" }}
          />
        </Field>
      ))}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <span style={{ fontSize: 12, color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Save size={12} /> {savedAt ? `Draft saved ${savedAt.toLocaleTimeString()}` : "Draft saves automatically as you type"}
        </span>
      </div>

      <FormFooter busy={busy} err={err} testid="buyer-info" />
    </form>
  );
}
